import {
  ShortEntityResponse,
  ConsortiumMatchV2,
} from 'app/modules/entities/responses';
import {
  U21Chip,
  U21TableColumn,
  U21TableBodyCellProps,
} from 'app/shared/u21-ui/components';
import { ROUTES_MAP } from 'app/shared/utils/routes';

import { IconArrowRight } from '@u21/tabler-icons';
import { EntityRiskScore } from 'app/modules/riskScore/components/EntityRiskScore';
import { ConsortiumSummaryPopover } from 'app/modules/consortium/components/ConsortiumSummaryPopover';
import { isEmpty } from 'lodash';
import { hasConsortiumAlerts } from 'app/modules/consortium/utils';
import { ConsortiumAlertsSummaryResponse } from 'app/modules/consortium/responses';
import { ConsortiumAlertsV2Response } from 'app/modules/consortium/responsesV2';
import { ConsortiumV2SummaryPopover } from 'app/modules/consortium/components/ConsortiumV2SummaryPopover';

type EntityTableColumn = U21TableColumn<ShortEntityResponse, any>;

export const RISK_SCORE_COLUMN: EntityTableColumn = {
  id: 'risk_score',
  disableSortBy: true,
  // We do not have the risk score for the entity, we have to fetch it once the column is rendered
  Cell: ({
    row: { external_id: externalId },
  }: U21TableBodyCellProps<ShortEntityResponse>) => {
    return <EntityRiskScore entityExternalId={externalId} />;
  },
};

export const GOTO_COLUMN: EntityTableColumn = {
  id: 'goto',
  type: 'button',
  cellProps: (entity) => ({
    children: <IconArrowRight />,
    to: ROUTES_MAP.entitiesId.path.replace(':id', String(entity.id)),
  }),
  disableSortBy: true,
};

export const ENTITY_COLUMN_CONFIG = {
  [RISK_SCORE_COLUMN.id]: RISK_SCORE_COLUMN,
};

export const getConsortiumV2Columns = <
  T extends ConsortiumMatchV2,
>(): U21TableColumn<T, any>[] => [
  {
    id: 'consortium_type',
    accessor: 'consortium_type',
    Header: 'Org type',
    type: 'text',
  },
  {
    id: 'registered_at',
    accessor: 'registered_at',
    Header: 'Registered at',
    type: 'datetime',
  },
  {
    id: 'blocked_reasons',
    accessor: 'blocked_reasons',
    Header: 'Blocked reasons',
    type: 'list',
    cellProps: {
      getShowLessText: () => 'Show fewer reasons',
      getShowMoreText: (length: number) => `Show all ${length} reasons`,
      displayFunc: (reason: string) => <U21Chip key={reason}>{reason}</U21Chip>,
    },
  },
  {
    id: 'last_blocked_at',
    accessor: 'last_blocked_at',
    Header: 'Last blocked at',
    type: 'datetime',
  },
];

export const getConsortiumSummaryColumn = (
  consortiumAlertsSummary: ConsortiumAlertsSummaryResponse,
): EntityTableColumn => {
  return {
    id: 'consortium_summary',
    type: 'button',
    Cell: ({
      row: { external_id: externalId },
    }: U21TableBodyCellProps<ShortEntityResponse>) => {
      const summary = consortiumAlertsSummary[externalId];
      if (!summary || isEmpty(summary) || !hasConsortiumAlerts(summary)) {
        return null;
      }
      return <ConsortiumSummaryPopover consortiumAlertsSummary={summary} />;
    },
    disableSortBy: true,
  };
};

export const getConsortiumV2SummaryColumn = (
  consortiumAlertsV2: ConsortiumAlertsV2Response,
): EntityTableColumn => {
  return {
    id: 'consortium_summary',
    type: 'button',
    Cell: ({
      row: { external_id: externalId },
    }: U21TableBodyCellProps<ShortEntityResponse>) => {
      const entityData = consortiumAlertsV2[externalId];
      if (!entityData || entityData.alerts.length === 0) {
        return null;
      }
      return (
        <ConsortiumV2SummaryPopover
          entityData={entityData}
          entityExternalId={externalId}
        />
      );
    },
    disableSortBy: true,
  };
};
